import {Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import {SupersetApiService, SupersetDashboardResponse, SupersetGuestTokenRequest} from '@npck/af-frontend-api';
import {embedDashboard, EmbeddedDashboard} from '@superset-ui/embedded-sdk';
import {firstValueFrom} from 'rxjs';

@Component({
    selector: 'af-superset-dashboard',
    standalone: true,
    imports: [TranslateModule],
    template: `
        @if (embedFailed) {
            <div class="superset-error">{{ 'i18n_af_supersetEmbedFailed' | translate }}</div>
        }
        <div #host class="superset-host" [class.hidden]="embedFailed"></div>
    `,
    styleUrl: './superset-dashboard.component.scss',
})
export class SupersetDashboardComponent implements OnChanges, OnDestroy {

    @ViewChild('host', {static: true}) host!: ElementRef<HTMLElement>;

    @Input() dashboard!: SupersetDashboardResponse;
    @Input() supersetDomain!: string;
    @Input() filterValue!: SupersetGuestTokenRequest;

    embedFailed = false;

    private embed?: EmbeddedDashboard;
    private embeddedKey?: string;

    constructor(private readonly api: SupersetApiService) {
    }

    ngOnChanges(): void {
        const key = JSON.stringify([this.supersetDomain, this.dashboard?.dashboardId, this.filterValue]);
        if (key === this.embeddedKey) {
            return;
        }
        this.embeddedKey = key;
        void this.reload();
    }

    ngOnDestroy(): void {
        this.embed?.unmount();
    }

    async reload(): Promise<void> {
        this.embed?.unmount();
        this.embed = undefined;
        this.embedFailed = false;
        const restoreMountPoint = this.grantFullscreenOnMount();
        try {
            this.embed = await embedDashboard({
                id: this.dashboard.dashboardId,
                supersetDomain: this.supersetDomain,
                mountPoint: this.host.nativeElement,
                fetchGuestToken: () => this.fetchGuestToken(),
                dashboardUiConfig: {hideTitle: true, filters: {visible: false}},
            });
        } catch (e) {
            this.failLocally(e);
        } finally {
            restoreMountPoint();
        }
    }

    private async fetchGuestToken(): Promise<string> {
        try {
            const response = await firstValueFrom(this.api.getSupersetGuestToken(this.filterValue));
            return response.token;
        } catch (e) {
            this.failLocally(e);
            return '';
        }
    }

    private failLocally(error: unknown): void {
        console.error('Superset dashboard embed failed', error);
        this.embedFailed = true;
        this.embeddedKey = undefined;
        this.embed?.unmount();
        this.embed = undefined;
    }

    private grantFullscreenOnMount(): () => void {
        const host = this.host.nativeElement;
        const originalReplaceChildren = host.replaceChildren;
        host.replaceChildren = (...nodes: (Node | string)[]): void => {
            nodes.filter(node => node instanceof HTMLIFrameElement).forEach(iframe => {
                iframe.allowFullscreen = true;
                iframe.setAttribute('allow', 'fullscreen');
            });
            originalReplaceChildren.apply(host, nodes);
        };
        return () => Reflect.deleteProperty(host, 'replaceChildren');
    }
}
